import type { ParsedUrlQuery } from 'querystring';
import qs from 'querystring';

import type { IAbTestingExperiments } from '@sravni/ab-testing-sdk/lib/browser';
import UTMZ from '@sravni/server-utils/lib/utmz';

import type { AffSub4 } from '@src/@types/microcredits';
import { Experiments } from '@src/constants/abTest';
import type { CookieModel } from '@src/modules/cookie';
import { Cookie, CookieKey } from '@src/modules/cookie';
import { getGaIdFromCookies, getYandexMetricsId } from '@src/utils/analytics';

import { getAffClickId, getAffId } from './helpers';

const TYPES = {
  microcredit: 'Микрокредиты',
};

const KEYS = {
  SUB: 'adv_sub',
  SOURCE: 'source',
  PATH: 'aff_sub2',
  UTM_MARKS: 'aff_sub3',
  PRODUCT: 'aff_sub4',
  TYPE: 'aff_sub5',
  AFF_CLICK: 'aff_click_id',
  AFF_ID: 'aff_id',
  GAID: 'aff_sub',
  USER_ID: 'aff_unique1',
  MIND_BOX_UUID: 'aff_unique2',
} as const;

export interface IMakePixelProps {
  sub?: string;
  type?: string;
  source?: string;
  mindboxUUID?: string;
  pathname: string;
  userId?: string;
  isWL?: boolean;
  route: string;
  pixelType?: string;
  abTestingSdkExperiments: IAbTestingExperiments | null;
  affSub4?: AffSub4;
  query: ParsedUrlQuery;
  affClickId?: string;
}

const updateLinkQuery = (link: string, query: PixelState) => {
  if (!link) {
    return null;
  }
  const [url, originalQueryString = ''] = link.split('?');

  const propertiesString = qs.stringify({
    ...qs.parse(originalQueryString),
    ...query,
  });

  return `${url}?${propertiesString}`;
};

interface IGetUTMZParams {
  url?: string;
  utmzCookie?: string;
}

const getUTMZ = ({ utmzCookie, url }: IGetUTMZParams) => {
  if (utmzCookie) {
    return utmzCookie;
  }

  if (!url) {
    return null;
  }

  const utmMarksObject = UTMZ.getFromUrl(decodeURIComponent(url));
  const utmMarksString = UTMZ.createCookieString(utmMarksObject);

  return encodeURIComponent(utmMarksString);
};

const getAbTestingSdkExperimentsValue = (abTestingSdkExperiments: IAbTestingExperiments | null) => {
  const mfoExperiments: string[] = Object.values(Experiments);

  return Object.entries(abTestingSdkExperiments || {})
    .filter(([key]) => mfoExperiments.includes(key))
    .map(([key, value]) => `${key}.${value}`)
    .join('|');
};

const getAffSub = (cookie: CookieModel, isWL: boolean | undefined) => {
  const yandexMetricsId = getYandexMetricsId(cookie, Boolean(isWL));
  const gaid = getGaIdFromCookies(cookie);
  const affSubGoogle = gaid && `ga_${gaid}`;
  const affSubYandex = yandexMetricsId && `ym_${yandexMetricsId}`;

  return [affSubGoogle, affSubYandex].filter(Boolean).join('|');
};

type PixelState = Partial<Record<(typeof KEYS)[keyof typeof KEYS], string>>;
// eslint-disable-next-line max-statements
export function makePixelState(props: IMakePixelProps): PixelState {
  const {
    sub,
    source = 'search',
    pathname, // is asPath исторически сложился этот кал
    userId,
    isWL,
    route, // is pathname исторически сложился этот кал
    abTestingSdkExperiments,
    affSub4,
    query,
    affClickId,
  } = props || {};

  const cookie = Cookie.get();
  const utmzCookie = cookie?.[CookieKey.__utmz];

  const utmz = getUTMZ({ url: pathname, utmzCookie });
  const affClId = getAffClickId({ cookie, affClickId });
  // да тут блядство с параметрами route=pathname
  // pathname=asPath
  const affId = getAffId({ pathname: route, asPath: pathname, query });

  const patch: PixelState = {
    [KEYS.SUB]: sub,
    [KEYS.TYPE]: TYPES.microcredit,
    [KEYS.SOURCE]: source,
    [KEYS.MIND_BOX_UUID]: cookie?.[CookieKey.mindboxUUid],
    [KEYS.GAID]: getAffSub(cookie, isWL),
  };

  if (pathname) {
    const [path] = pathname.split('?');
    patch[KEYS.PATH] = path.endsWith('/') ? path : `${path}/`;
  }

  if (affSub4) {
    patch[KEYS.PRODUCT] = Object.values(affSub4).join('|');
  }

  if (userId) {
    patch[KEYS.USER_ID] = userId;
  }

  if (utmz) {
    patch[KEYS.UTM_MARKS] = utmz;
  }

  if (abTestingSdkExperiments) {
    const abTestingSdkExperimentsValue = getAbTestingSdkExperimentsValue(abTestingSdkExperiments);

    patch[KEYS.TYPE] += abTestingSdkExperimentsValue && `|AB_${abTestingSdkExperimentsValue}`;
  }

  if (affId) {
    patch[KEYS.AFF_ID] = affId;
  }

  if (affClId) {
    patch[KEYS.AFF_CLICK] = affClId;
  }

  return patch;
}

export function updatePixelState(
  link: string,
  prev: ReturnType<typeof makePixelState>,
  next: Partial<IMakePixelProps>,
) {
  const { sub, source = 'search', affSub4, affClickId } = next || {};
  const cookie = Cookie.get();

  const state = {
    ...prev,
    [KEYS.SUB]: sub,
    [KEYS.SOURCE]: source,
    [KEYS.PRODUCT]: affSub4 ? Object.values(affSub4).join('|') : undefined,
    [KEYS.AFF_CLICK]: affClickId || prev[KEYS.AFF_CLICK],
    // пришлось его добавить в обновление потому что
    // тэг Гугл аналитики проставляется поздно на странице после networkIdle
    [KEYS.GAID]: getAffSub(cookie, true),
  };
  return updateLinkQuery(link, state);
}
